'use client'
import { useState } from 'react'
import { VStack } from '@chakra-ui/react'
import type {
  CreateInvoiceOrderDTO,
  NshiftCheckoutWidgetResult,
  OrderShippingAdditionalDataDTO,
  ShipmentDTO
} from 'ecosystem'
import { useBuyerShipping } from '../../../../common/hooks'
import { useCheckoutContext } from '../../../hooks'
import { NshiftCheckout } from '../../NshiftCheckout'
import { BillingAddressPicker, useBillingAddress } from '../DefaultDelivery'
import { PaymentWithDelivery } from '../PaymentWithDelivery'
import { InvoiceReviewPanel } from './InvoiceReviewPanel'
import { useDebouncedDraftSync } from './useDebouncedDraftSync'

const widgetResultToShippingCost = (
  result: NshiftCheckoutWidgetResult | undefined
): number | undefined => {
  if (!result) return undefined
  // Walk known shapes — some Nshift versions surface the price under different keys.
  const r = result as unknown as {
    price?: number
    selectedShippingOption?: { price?: number }
    selectedOption?: { price?: number }
  }
  return r.selectedShippingOption?.price ?? r.selectedOption?.price ?? r.price
}

interface InvoiceAndNshiftProps {
  submitPath: string
}

export const InvoiceAndNshift = ({ submitPath }: InvoiceAndNshiftProps) => {
  const { orderResponse, invoiceOrderResponse, paymentProviderConfig } = useCheckoutContext()
  const { rebaseShippingQuote } = useBuyerShipping(paymentProviderConfig)
  const [shippingCost, setShippingCost] = useState<number | undefined>(undefined)
  // const [shippingAddress, setShippingAddress] = useState<OrderAddressDTO | undefined>(undefined)
  const [nshiftDetails, setNshiftDetails] = useState<
    OrderShippingAdditionalDataDTO['nshiftDetails'] | undefined
  >(undefined)
  const [isValid, setIsValid] = useState(false)
  const { billingAddress, setBillingAddress } = useBillingAddress(
    invoiceOrderResponse?.storefrontOrderResponse?.billingAddress
  )

  const shipment: ShipmentDTO = {
    shippingProvider: 'NSHIFT',
    ...(orderResponse?.nshiftResponse?.sessionId
      ? { shipmentId: orderResponse.nshiftResponse.sessionId }
      : {}),
    ...(nshiftDetails
      ? {
          shippingType: nshiftDetails.isPickupFromStore ? 'PICKUP' : 'DELIVERY',
          additionalInfo: { nshiftDetails }
        }
      : {})
  }

  const draftOverrides: Partial<CreateInvoiceOrderDTO> = {
    ...(shippingCost !== undefined ? { shippingCost } : {}),
    shipments: [shipment],
    ...(billingAddress ? { billingAddress } : {})
  }

  const { flush: flushDraftSync } = useDebouncedDraftSync(draftOverrides, isValid)

  return (
    <PaymentWithDelivery
      deliveryComponent={
        <NshiftCheckout
          onDataChange={(data, meta) => {
            if (meta.widgetResultChanged || meta.totalPriceChanged) {
              setShippingCost(
                rebaseShippingQuote(
                  widgetResultToShippingCost(data.widgetResult) ?? data.totalPrice
                )
              )
            }
            setNshiftDetails({
              isPickupFromStore: data.isPickupFromStore,
              widgetResult: data.widgetResult,
              recipientAddress: data.recipientAddress
            })
            setIsValid(meta.isValid)
          }}
        />
      }
      paymentComponent={
        <VStack align="stretch" gap={4} maxW="600px" w="100%">
          <BillingAddressPicker onChange={setBillingAddress} value={billingAddress} />
          <InvoiceReviewPanel
            draftOverrides={draftOverrides}
            flushDraftSync={flushDraftSync}
            isDisabled={!isValid}
            submitPath={submitPath}
          />
        </VStack>
      }
    />
  )
}
